import { create } from 'zustand';
import { api, AUTH_EXPIRED_EVENT, TOKEN_KEY, unwrap } from '../api/client';
import type { AuthSession, User } from '../api/types';

// Auth store (skill aerostores-security): access JWT in sessionStorage, refresh token in an httpOnly cookie
// (credentials: 'include'), user + permission codes from /auth/me. UI hides, API enforces.
interface AuthState {
  token: string | null;
  user: User | null;
  /** true once the token in sessionStorage has been validated (or found absent) after a page load */
  hydrated: boolean;
  signIn: (username: string, password: string) => Promise<User>;
  signOut: () => Promise<void>;
  refresh: () => Promise<boolean>;
  hydrate: () => Promise<void>;
  hasPermission: (code: string) => boolean;
}

const readToken = (): string | null => {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

const writeToken = (token: string | null): void => {
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage unavailable */
  }
};

let refreshing: Promise<boolean> | null = null;

export const useAuth = create<AuthState>()((set, get) => {
  const setSession = (s: AuthSession) => {
    writeToken(s.accessToken);
    set({ token: s.accessToken, user: s.user, hydrated: true });
  };
  const clearSession = () => {
    writeToken(null);
    set({ token: null, user: null, hydrated: true });
  };
  /** POST /auth/refresh (deduplicated); on success the new access token replaces the old one. */
  const refresh = (): Promise<boolean> => {
    refreshing ??= (async () => {
      try {
        const s = await unwrap(api.POST('/api/v1/auth/refresh', { credentials: 'include' }));
        setSession(s);
        return true;
      } catch {
        clearSession();
        return false;
      } finally {
        refreshing = null;
      }
    })();
    return refreshing;
  };

  return {
    token: readToken(),
    user: null,
    hydrated: false,
    async signIn(username, password) {
      const s = await unwrap(api.POST('/api/v1/auth/login', { body: { username, password }, credentials: 'include' }));
      setSession(s);
      return s.user;
    },
    async signOut() {
      try {
        await api.POST('/api/v1/auth/logout', { credentials: 'include' });
      } catch {
        /* network down — still drop the local session */
      }
      clearSession();
    },
    refresh,
    async hydrate() {
      if (!get().token) {
        set({ hydrated: true });
        return;
      }
      const me = await api.GET('/api/v1/auth/me');
      if (me.data) {
        set({ user: me.data, hydrated: true });
        return;
      }
      if (me.response.status === 401 && (await refresh())) {
        const again = await api.GET('/api/v1/auth/me');
        if (again.data) {
          set({ user: again.data, hydrated: true });
          return;
        }
      }
      clearSession();
    },
    hasPermission: (code) => get().user?.permissions.includes(code) ?? false,
  };
});

/** Current user (null while anonymous). */
export const useCurrentUser = (): User | null => useAuth((s) => s.user);

/** Registers the `auth:expired` handler: refresh once, otherwise sign out (the route guard then redirects to /login). */
export function installAuthListeners(): () => void {
  const onExpired = () => {
    void useAuth
      .getState()
      .refresh()
      .then((ok) => (ok ? undefined : useAuth.getState().signOut()));
  };
  window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
  return () => {
    window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  };
}
